import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';
import { Component } from '@theme/component';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends Component {
  #parentDetails = null;

  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    // Skip when the panel is inside a closed details element — position is irrelevant
    if (this.#parentDetails && !this.#parentDetails.open) return;

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    this.style.top = OFFSET + 'px';

    if (rect.width === 0) return;

    // Convert viewport-relative overflow into containing-block-relative left offset.
    // rect.left - parentLeft gives the element's current left edge in CB coordinates,
    // equivalent to offsetLeft but accounting for any CB border/scroll offset.
    const parentLeft = (this.offsetParent ?? document.documentElement).getBoundingClientRect().left;

    if (rect.right > viewportWidth) {
      const overflowAmount = rect.right - viewportWidth + OFFSET;
      this.style.right = 'auto';
      this.style.left = `${rect.left - parentLeft - overflowAmount}px`;
    }

    if (rect.left < 0) {
      const overflowAmount = Math.abs(rect.left) + OFFSET;
      this.style.right = 'auto';
      this.style.left = `${rect.left - parentLeft + overflowAmount}px`;
    }

    this.#mutationObserver.takeRecords();
  };

  #onParentToggle = (event) => {
    if (event.target instanceof HTMLDetailsElement && event.target.open) {
      this.#updatePosition();
    }
  };

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
    }
  }, 100);

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.#resizeListener);

    this.#parentDetails = this.closest('details');
    if (this.#parentDetails) {
      this.#parentDetails.addEventListener('toggle', this.#onParentToggle);
    }

    requestIdleCallback(() => {
      this.#updatePosition();
      this.#mutationObserver.observe(this, { attributes: true });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#mutationObserver.disconnect();
    if (this.#parentDetails) {
      this.#parentDetails.removeEventListener('toggle', this.#onParentToggle);
      this.#parentDetails = null;
    }
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
